/**
 * TwinCardFan — portrait cards fanned out like a hand of cards.
 *
 * The compose modal's "Who do you want to talk to?" hero and the empty
 * conversation (the room's own twins) share it. Cards spread evenly around
 * the center, the middle one on top; three cards reproduce the original
 * modal layout exactly (-8deg / 0 / 8deg, 55% apart).
 *
 * With `renderCard`, each card is wrapped by the caller (e.g. a profile hover
 * card) and lifts while hovered or focused. With `dealIn`, the cards arrive one
 * after another on mount, like a hand being dealt.
 */

import { FocusEvent, Fragment, ReactElement, ReactNode, forwardRef, useState } from 'react'
import { motion } from 'framer-motion'

import { UserAvatar } from '@/components/ui/UserAvatar'
import { usePersonaThumbFallback } from '@/components/shared/PersonaThumbImg'
import { cn } from '@/lib/utils'
import { getUserInitials } from '@/lib/utils/avatar'

const CARD_WIDTH_REM = 7
const SPREAD = 0.55
const ROTATE_STEP_DEG = 8
const SIDE_MARGIN_REM = 3.3
const DEAL_STAGGER_S = 0.04

export interface TwinCardFanCard {
  id: string
  imageUrl?: string | null
  /** Used for the initials fallback and the accessible label. */
  name?: string
}

interface TwinCardFanProps {
  cards: Array<TwinCardFanCard>
  /** Shown as a "+N" badge on the last card when more twins exist than cards. */
  overflowCount?: number
  /** Wraps each card (e.g. in a profile hover card); cards become focusable buttons. */
  renderCard?: (index: number, card: ReactElement) => ReactNode
  /** Persona portraits go through the thumbnail fallback; static art does not need it. */
  isPersonaImage?: boolean
  /** Deal the cards in one after another on mount. */
  dealIn?: boolean
  className?: string
}

export function TwinCardFan(props: TwinCardFanProps) {
  const { cards, overflowCount = 0, renderCard, isPersonaImage = false, dealIn = false, className } = props

  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  const count = cards.length
  const widthRem = CARD_WIDTH_REM + (count - 1) * CARD_WIDTH_REM * SPREAD + SIDE_MARGIN_REM

  return (
    <div
      className={cn('relative flex h-44 items-center justify-center', className)}
      style={{ width: `${widthRem}rem`, maxWidth: '100%' }}
      data-testid="twin-card-fan"
    >
      {cards.map((card, index) => {
        const offset = index - (count - 1) / 2
        const isActive = activeIndex === index
        const zIndex = isActive ? 100 : (count - Math.abs(offset) * 2) * 2 + (offset > 0 ? 1 : 0)
        const lift = isActive ? ' translateY(-6px)' : ''
        const isLast = index === count - 1

        const cardElement = (
          <FanCard
            card={card}
            index={index}
            isInteractive={Boolean(renderCard)}
            isPersonaImage={isPersonaImage}
            overflowCount={isLast ? overflowCount : 0}
            style={{
              transform: `translateX(${offset * SPREAD * 100}%) rotate(${offset * ROTATE_STEP_DEG}deg)${lift}`,
              zIndex,
            }}
            onActiveChange={(isOn) => setActiveIndex(isOn ? index : null)}
          />
        )

        const wrapped = renderCard ? renderCard(index, cardElement) : cardElement

        if (!dealIn) {
          return <Fragment key={card.id}>{wrapped}</Fragment>
        }

        // The card keeps its own fan transform; the deal moves this full-size layer,
        // which lets pointer events through to the cards beneath it.
        return (
          <motion.div
            key={card.id}
            className="pointer-events-none absolute inset-0 flex items-center justify-center [&>*]:pointer-events-auto"
            style={{ zIndex }}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: index * DEAL_STAGGER_S, ease: [0.32, 0.72, 0, 1] }}
          >
            {wrapped}
          </motion.div>
        )
      })}
    </div>
  )
}

interface FanCardProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'style'> {
  card: TwinCardFanCard
  index: number
  isInteractive: boolean
  isPersonaImage: boolean
  overflowCount: number
  style: React.CSSProperties
  onActiveChange: (isActive: boolean) => void
}

// forwardRef + spread: a hover-card trigger (Radix asChild) hands the card its ref and handlers.
const FanCard = forwardRef<HTMLButtonElement, FanCardProps>(function FanCard(props, ref) {
  const { card, index, isInteractive, isPersonaImage, overflowCount, style, onActiveChange, ...triggerProps } = props

  const { displayUrl, handleImageError } = usePersonaThumbFallback(isPersonaImage ? card.imageUrl : null, 256)
  const [hasImageFailed, setHasImageFailed] = useState(false)

  const imageUrl = isPersonaImage ? displayUrl : card.imageUrl
  const showImage = Boolean(imageUrl) && !hasImageFailed

  const className = cn(
    'absolute h-36 w-28 overflow-hidden rounded-2xl border-2 border-background shadow-xl',
    isInteractive &&
      'cursor-pointer transition-transform duration-200 ease-out motion-reduce:transition-none focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
  )

  const content = (
    <>
      {showImage ? (
        <img
          src={imageUrl ?? undefined}
          alt=""
          loading="eager"
          className={cn('h-full w-full object-cover', isPersonaImage && 'object-top')}
          onError={() => (isPersonaImage ? handleImageError(() => setHasImageFailed(true)) : setHasImageFailed(true))}
        />
      ) : (
        <span className="flex h-full w-full items-center justify-center bg-twin-chat-bubble/30">
          <UserAvatar initials={getUserInitials(card.name)} name={card.name ?? ''} size="lg" />
        </span>
      )}
      {overflowCount > 0 && (
        <span
          className="absolute bottom-2 right-2 rounded-full bg-background/90 px-2 py-0.5 text-xs font-medium text-foreground shadow-sm"
          data-testid="twin-card-fan-overflow"
        >
          +{overflowCount}
        </span>
      )}
    </>
  )

  if (!isInteractive) {
    return (
      <div className={className} style={style}>
        {content}
      </div>
    )
  }

  return (
    <button
      type="button"
      {...triggerProps}
      ref={ref}
      className={className}
      style={style}
      aria-label={card.name}
      data-testid={`twin-card-fan-card-${index}`}
      onMouseEnter={(event) => {
        triggerProps.onMouseEnter?.(event)
        onActiveChange(true)
      }}
      onMouseLeave={(event) => {
        triggerProps.onMouseLeave?.(event)
        onActiveChange(false)
      }}
      onFocus={(event: FocusEvent<HTMLButtonElement>) => {
        triggerProps.onFocus?.(event)
        onActiveChange(true)
      }}
      onBlur={(event: FocusEvent<HTMLButtonElement>) => {
        triggerProps.onBlur?.(event)
        onActiveChange(false)
      }}
    >
      {content}
    </button>
  )
})
